import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { FaPlus, FaYoutube, FaFilePdf, FaFilePowerpoint, FaBook, FaVideo, FaTrash, FaPlay, FaEye, FaShare } from "react-icons/fa";
import AddCoursePopup from "./AddCoursePopup";
import QuizModal from "./QuizModal";
import CourseVideoShare from "./CourseVideoShare";
import { authHeaders } from "../utils/authHeaders";
import "./KursusOnlineTab.css";

const DOCUMENT_TYPES = [
  "e_book",
  "panduan_pembelajaran",
  "panduan_fasilitasi",
  "materi_presentasi",
  "flipchart",
];

const ROLE_OPTIONS = [
  "Keluarga Petani",
  "Penggerak Pemuda",
  "Agripreneur",
  "Fasilitator Lapangan",
  "Supervisor",
];

const KELUARGA_PETANI_DOC_TYPES = ["e_book", "panduan_pembelajaran"];

function getYouTubeId(url) {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = String(url || "").match(regExp);
  return match && match[2]?.length === 11 ? match[2] : null;
}

function getGoogleDriveFileId(url) {
  const raw = String(url || "").trim();
  if (!raw) return null;
  const patterns = [
    /drive\.google\.com\/file\/d\/([^/]+)/i,
    /drive\.google\.com\/open\?id=([^&]+)/i,
    /drive\.google\.com\/uc\?id=([^&]+)/i,
    /docs\.google\.com\/file\/d\/([^/]+)/i,
    /[?&]id=([^&]+)/i,
  ];
  for (const pattern of patterns) {
    const match = raw.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
}

function getEmbeddedVideoSrc(url) {
  const ytId = getYouTubeId(url);
  if (ytId) return `https://www.youtube.com/embed/${ytId}`;
  const driveId = getGoogleDriveFileId(url);
  return driveId ? `https://drive.google.com/file/d/${driveId}/preview` : null;
}

function getVideoSessionId() {
  let sessionId;
  try {
    sessionId = sessionStorage.getItem("course_video_session");
  } catch {}
  if (!/^[A-Za-z0-9_-]{16,80}$/.test(sessionId || "")) {
    sessionId = window.crypto?.randomUUID?.() || `video-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      sessionStorage.setItem("course_video_session", sessionId);
    } catch {}
  }
  return sessionId;
}

export default function KursusOnlineTab() {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";

  const [competencies, setCompetencies] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCompetency, setSelectedCompetency] = useState("");
  const [competencySearch, setCompetencySearch] = useState("");
  const [showCompetencyDropdown, setShowCompetencyDropdown] = useState(false);
  const [selectedRole, setSelectedRole] = useState(ROLE_OPTIONS[0]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noCoursesForCompetency, setNoCoursesForCompetency] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [shareCourse, setShareCourse] = useState(null);
  const [sharedVideoError, setSharedVideoError] = useState("");
  const sharedVideoHandledRef = useRef(false);
  const competencyFilterRef = useRef(null);
  const videoSessionRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  useEffect(() => {
    fetchCompetencies();
    fetchCourses();
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [selectedCompetency]);

  useEffect(() => {
    if (!selectedCompetency) {
      setCompetencySearch("");
      return;
    }
    const activeCompetency = competencies.find((comp) => String(comp.id) === String(selectedCompetency));
    setCompetencySearch(activeCompetency?.name || "");
  }, [selectedCompetency, competencies]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!competencyFilterRef.current?.contains(event.target)) {
        setShowCompetencyDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchCompetencies = async () => {
    try {
      const response = await fetch(`${apiUrl}/capdev/competencies/all`);
      const data = await response.json();
      if (data.success) {
        setCompetencies(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching competencies:", error);
    }
  };

  const fetchCourses = async () => {
    setLoading(true);
    setNoCoursesForCompetency(false);
    try {
      let url = `${apiUrl}/courses`;
      if (selectedCompetency) {
        url += `?competency_id=${selectedCompetency}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setCourses(data.data || []);
        if (selectedCompetency && (!data.data || data.data.length === 0)) {
          setNoCoursesForCompetency(true);
        } else {
          setNoCoursesForCompetency(false);
        }
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = (newCourse) => {
    if (newCourse.capability_id) {
      setSelectedCompetency(newCourse.capability_id.toString());
    } else {
      setSelectedCompetency("");
    }
    fetchCourses();
    setShowAddModal(false);
  };

  const handleQuizComplete = (score, courseId) => {
    localStorage.setItem(`course_${courseId}_score`, score);
    setShowQuizModal(false);
    fetchCourses();
  };


  const handleDeleteCourse = async (courseId) => {
    if (!isAdmin) return;

    const confirmed = window.confirm("Hapus konten ini? Tindakan ini tidak dapat dibatalkan.");
    if (!confirmed) return;

    try {
      const response = await fetch(`${apiUrl}/courses/${courseId}`, {
        method: "DELETE",
        headers: authHeaders(user),
        credentials: "include",
      });
      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Gagal menghapus konten.");
      }

      if (showDocModal && currentDoc) {
        setShowDocModal(false);
        setCurrentDoc(null);
      }

      await fetchCourses();
    } catch (error) {
      console.error("Delete course error:", error);
      alert(error?.message || "Gagal menghapus konten.");
    }
  };

  const getMobileOfflineUrl = (url) => {
    const driveId = getGoogleDriveFileId(url);
    if (driveId) return `https://drive.google.com/uc?export=download&id=${driveId}`;
    return url;
  };

  const getDocPreviewSrc = (url) => {
    const raw = String(url || "").trim();
    if (!raw) return null;

    const driveId = getGoogleDriveFileId(raw);
    if (driveId) {
      return `https://drive.google.com/file/d/${driveId}/preview`;
    }

    if (/\.pdf([?#].*)?$/i.test(raw)) {
      return raw;
    }

    return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(raw)}`;
  };

  const getDocDownloadUrl = (url) => {
    const raw = String(url || "").trim();
    const driveId = getGoogleDriveFileId(raw);
    if (driveId) {
      return `https://drive.google.com/uc?export=download&id=${driveId}`;
    }
    return raw;
  };

  const openDocumentPreview = (course) => {
    setCurrentDoc({
      title: course.title,
      src: getDocPreviewSrc(course.url),
      downloadUrl: getDocDownloadUrl(course.url),
      originalUrl: course.url,
    });
    setShowDocModal(true);
  };

  const recordVideoView = useCallback(async (course) => {
    if (!videoSessionRef.current) videoSessionRef.current = getVideoSessionId();
    try {
      const response = await fetch(`${apiUrl}/courses/${course.id}/view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: videoSessionRef.current }),
        keepalive: true,
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Gagal mencatat view video.");
      setCourses((previous) => previous.map((item) => (
        item.id === course.id ? { ...item, view_count: Math.max(Number(item.view_count) || 0, Number(data.view_count) || 0) } : item
      )));
    } catch (error) {
      console.error("Course video view error:", error);
    }
  }, [apiUrl]);

  const openVideoPreview = useCallback((course) => {
    recordVideoView(course);
    setCurrentVideo({
      title: course.title,
      src: getEmbeddedVideoSrc(course.url),
    });
  }, [recordVideoView]);

  const closeShare = useCallback(() => setShareCourse(null), []);

  useEffect(() => {
    if (loading || sharedVideoHandledRef.current) return;
    const videoId = new URLSearchParams(window.location.search).get("video");
    if (!videoId) return;
    sharedVideoHandledRef.current = true;
    const course = courses.find((item) => String(item.id) === videoId && ["video_pembelajaran", "video_tutorial"].includes(item.type));
    if (!course) {
      setSharedVideoError("Video yang dibagikan tidak ditemukan atau sudah tidak tersedia.");
      return;
    }
    if (getEmbeddedVideoSrc(course.url)) openVideoPreview(course);
    else setCurrentVideo({ title: course.title, src: null, originalUrl: course.url });
  }, [courses, loading, openVideoPreview]);

  const isPassed = (courseId) => {
    const score = localStorage.getItem(`course_${courseId}_score`);
    return score && parseFloat(score) >= 80;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "video_pembelajaran":
      case "video_tutorial":
        return <FaYoutube className="type-icon" />;
      case "panduan_pembelajaran":
      case "e_book":
      case "panduan_fasilitasi":
        return <FaFilePdf className="type-icon" />;
      case "materi_presentasi":
        return <FaFilePowerpoint className="type-icon" />;
      case "flipchart":
        return <FaBook className="type-icon" />;
      default:
        return <FaVideo className="type-icon" />;
    }
  };

  if (loading) {
    return <div className="loading">Memuat kursus...</div>;
  }

  const groupedByCompetency = {};
  courses.forEach((course) => {
    const compName = course.competency_name || "Umum";
    if (!groupedByCompetency[compName]) {
      groupedByCompetency[compName] = [];
    }
    groupedByCompetency[compName].push(course);
  });

  const categorizeCourses = (allCourses) => {
    const learningVideos = allCourses.filter((c) => c.type === "video_pembelajaran");
    const tutorialVideos = allCourses.filter((c) => c.type === "video_tutorial");
    const allowedDocTypes = selectedRole === "Keluarga Petani"
      ? KELUARGA_PETANI_DOC_TYPES
      : DOCUMENT_TYPES;

    const documents = allCourses
      .filter((c) => allowedDocTypes.includes(c.type))
      .sort((a, b) => DOCUMENT_TYPES.indexOf(a.type) - DOCUMENT_TYPES.indexOf(b.type));
    return { learningVideos, tutorialVideos, documents };
  };

  const handleCompetencyInputChange = (value) => {
    setCompetencySearch(value);
    setShowCompetencyDropdown(true);

    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue || normalizedValue === "semua kompetensi") {
      setSelectedCompetency("");
      return;
    }

    const matchedCompetency = competencies.find(
      (comp) => comp.name.toLowerCase() === normalizedValue
    );

    if (matchedCompetency) {
      setSelectedCompetency(String(matchedCompetency.id));
    }
  };

  const visibleCompetencies = competencies.filter((comp) =>
    comp.name.toLowerCase().includes(competencySearch.trim().toLowerCase())
  );

  const selectCompetencyOption = (competency) => {
    if (!competency) {
      setSelectedCompetency("");
      setCompetencySearch("");
    } else {
      setSelectedCompetency(String(competency.id));
      setCompetencySearch(competency.name);
    }
    setShowCompetencyDropdown(false);
  };

  const renderCourseCard = (course, compact = false) => {
    const isVideo = course.type === "video_pembelajaran" || course.type === "video_tutorial";

    return (
      <div key={course.id} className={`course-card ${!isVideo && compact ? "doc-course-card" : ""}`}>
        <div className={`course-card-head ${!isVideo && compact ? "doc-course-card-head" : ""}`}>
          <h3>{course.title}</h3>
          <div className="course-card-actions">
            {isAdmin && (
              <button
                type="button"
                className="course-delete-btn"
                onClick={() => handleDeleteCourse(course.id)}
                title="Hapus konten"
                aria-label="Hapus konten"
              >
                <FaTrash />
              </button>
            )}
            {!isVideo && (
              <button
                type="button"
                className="download-link course-open-btn"
                onClick={() => openDocumentPreview(course)}
              >
                Buka Materi
              </button>
            )}
          </div>
        </div>

        <div className="course-type">
          {getTypeIcon(course.type)} {course.type.replace(/_/g, " ").toUpperCase()}
          {course.competency_name && <span className="competency-tag">({course.competency_name})</span>}
        </div>

        {isVideo ? (
          <div className="video-embed">
            {(() => {
              const driveId = getGoogleDriveFileId(course.url);
              const youtubeId = getYouTubeId(course.url);
              if (driveId || youtubeId) {
                return (
                  <button
                    type="button"
                    className="drive-video-thumb"
                    onClick={() => openVideoPreview(course)}
                    aria-label={`Putar ${course.title}`}
                  >
                    <img
                      src={driveId
                        ? `https://drive.google.com/thumbnail?id=${driveId}&sz=w1000`
                        : `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
                      alt=""
                      loading="lazy"
                    />
                    <span className="drive-video-play" aria-hidden="true">
                      <FaPlay />
                    </span>
                  </button>
                );
              }

              const embedSrc = getEmbeddedVideoSrc(course.url);
              if (!embedSrc) {
                return (
                  <a href={course.url} target="_blank" rel="noopener noreferrer" className="external-link" onClick={() => recordVideoView(course)}>
                    Tonton Video
                  </a>
                );
              }
              return (
                <iframe
                  width="100%"
                  src={embedSrc}
                  title={course.title}
                  frameBorder="0"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              );
            })()}
          </div>
        ) : null}

        {isVideo && (
          <div className="course-video-meta">
            <div className="course-video-views" title="Jumlah sesi yang membuka video di Sahabat Kakao">
              <FaEye aria-hidden="true" />
              <span>{new Intl.NumberFormat("id-ID").format(Number(course.view_count) || 0)} kali dilihat</span>
            </div>
            <button type="button" className="course-video-share-btn" onClick={() => setShareCourse(course)} aria-label={`Bagikan ${course.title}`} title="Bagikan video">
              <FaShare aria-hidden="true" />
            </button>
          </div>
        )}

        {isVideo && (
          <a
            href={getMobileOfflineUrl(course.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="mobile-offline-btn"
          >
            Unduh Video (Offline)
          </a>
        )}

        {course.test_questions && (
          <button
            onClick={() => {
              setCurrentCourse(course);
              setShowQuizModal(true);
            }}
            className="test-btn"
            disabled={isPassed(course.id)}
          >
            {isPassed(course.id) ? "Tes Selesai (Lulus)" : "Kerjakan Tes"}
          </button>
        )}

        {isPassed(course.id) && !isVideo && (
          <div className="downloads">
            <a href={course.url} target="_blank" rel="noopener noreferrer" download className="download-btn">
              Download Materi
            </a>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="kursus-container">
      {sharedVideoError && <p role="status">{sharedVideoError}</p>}
      <div className="kursus-header kursus-sticky-controls">
        <div className="kursus-filter-block">
          <div className="kursus-filter-controls">
            <label className="kursus-filter-item">
              <span className="kursus-filter-title">Filter Kompetensi</span>
              <div className="kompetensi-combobox" ref={competencyFilterRef}>
                <input
                  type="text"
                  value={competencySearch}
                  onChange={(e) => handleCompetencyInputChange(e.target.value)}
                  onFocus={() => setShowCompetencyDropdown(true)}
                  className="competency-filter"
                  placeholder="Semua Kompetensi"
                />
                <button
                  type="button"
                  className="kompetensi-combobox-toggle"
                  onClick={() => setShowCompetencyDropdown((prev) => !prev)}
                  aria-label="Tampilkan daftar kompetensi"
                >
                  <span className="kompetensi-combobox-chevron" aria-hidden="true" />
                </button>

                {showCompetencyDropdown && (
                  <div className="kompetensi-combobox-menu">
                    <button
                      type="button"
                      className={`kompetensi-combobox-option ${!selectedCompetency ? "active" : ""}`}
                      onClick={() => selectCompetencyOption(null)}
                    >
                      Semua Kompetensi
                    </button>
                    {visibleCompetencies.map((comp) => (
                      <button
                        key={comp.id}
                        type="button"
                        className={`kompetensi-combobox-option ${String(comp.id) === String(selectedCompetency) ? "active" : ""}`}
                        onClick={() => selectCompetencyOption(comp)}
                      >
                        {comp.name}
                      </button>
                    ))}
                    {visibleCompetencies.length === 0 && (
                      <div className="kompetensi-combobox-empty">Kompetensi tidak ditemukan.</div>
                    )}
                  </div>
                )}
              </div>
            </label>

            <label className="kursus-filter-item kursus-role-item">
              <span className="kursus-filter-title">Role di Komunitas</span>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="competency-filter role-filter"
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {isAdmin && (
          <div className="kursus-add-row">
            <button onClick={() => setShowAddModal(true)} className="add-btn">
              <FaPlus /> Tambah Konten
            </button>
          </div>
        )}
      </div>

      {noCoursesForCompetency ? (
        <p>Tidak ada kursus tersedia. {isAdmin && "Tambahkan konten pertama!"}</p>
      ) : (
        Object.entries(groupedByCompetency).map(([compName, compCourses]) => {
          const { learningVideos, tutorialVideos, documents } = categorizeCourses(compCourses);
          return (
            <div key={compName} className="competency-group">
              <h3 className="competency-title">{compName}</h3>
              <div className="competency-cards-container">
                <div className="competency-card">
                  {learningVideos.length > 0 ? learningVideos.map(renderCourseCard) : <p>Tidak ada video pembelajaran.</p>}
                </div>
                <div className="competency-card">
                  {tutorialVideos.length > 0 ? tutorialVideos.map(renderCourseCard) : <p>Tidak ada video tutorial.</p>}
                </div>
                <div className="competency-card">
                  {documents.length > 0 ? documents.map((course) => renderCourseCard(course, true)) : <p>Tidak ada dokumen.</p>}
                </div>
              </div>
            </div>
          );
        })
      )}

      {showDocModal && currentDoc && (
        <div className="modal-overlay doc-preview-overlay" onClick={() => setShowDocModal(false)}>
          <div className="modal-content doc-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header doc-modal-header">
              <h3>{currentDoc.title}</h3>
              <div className="doc-header-actions">
                <a
                  href={currentDoc.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="download-btn"
                >
                  Download Materi
                </a>
                <button className="close-btn" onClick={() => setShowDocModal(false)}>
                  x
                </button>
              </div>
            </div>

            <div className="doc-preview-body">
              {currentDoc.src ? (
                <iframe
                  src={currentDoc.src}
                  title={`preview-${currentDoc.title}`}
                  className="doc-preview-frame"
                  frameBorder="0"
                />
              ) : (
                <div className="doc-preview-empty">
                  Preview tidak tersedia untuk dokumen ini.
                  <a href={currentDoc.originalUrl} target="_blank" rel="noopener noreferrer" className="external-link">
                    Buka di tab baru
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {currentVideo && (
        <div className="modal-overlay video-preview-overlay" onClick={() => setCurrentVideo(null)}>
          <div className="modal-content video-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header doc-modal-header">
              <h3>{currentVideo.title}</h3>
              <button className="close-btn" onClick={() => setCurrentVideo(null)}>
                x
              </button>
            </div>
            {currentVideo.src ? <iframe
              src={currentVideo.src}
              title={`video-${currentVideo.title}`}
              className="video-preview-frame"
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
              sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            /> : <a href={currentVideo.originalUrl} target="_blank" rel="noopener noreferrer" className="external-link" onClick={() => {
              const course = courses.find((item) => item.url === currentVideo.originalUrl);
              if (course) recordVideoView(course);
            }}>Tonton Video</a>}
          </div>
        </div>
      )}

      {showAddModal && (
        <AddCoursePopup
          competencies={competencies}
          userRole={user?.role}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddCourse}
        />
      )}

      {shareCourse && <CourseVideoShare course={shareCourse} onClose={closeShare} />}

      {showQuizModal && currentCourse && (
        <QuizModal
          course={currentCourse}
          onClose={() => setShowQuizModal(false)}
          onComplete={handleQuizComplete}
        />
      )}
    </div>
  );
}


